import Record from "./record";
import Entity from "./entity";
import DocumentsCache from "./documents-cache";
import Collection from "./collection";
import Api from "./api";
import IRecordResponse from "../interface/record-response";
import INotifHandler from "../interface/notif-handler";
import ICollectionResponse from "../interface/collection-response";
import IApiNotifDico from "../interface/api/api-notif-dico";
import IApiNotifData from "../interface/api/api-notif-data";
import IApiNotifBase from "../interface/api/api-notif-base";
import ParamStorage from "../enum/param-storage";
import ErrorCode from "../enum/error-code";
import ApiNotifType from "../enum/api-notif-type";
import ApiNotifMsgType from "../enum/api-notif-msg-type";
import ApiNotifDataOperation from "../enum/api-notif-data-operation";

class NotificationInstance {

    private readonly NotifType: number = ApiNotifType.Dico | ApiNotifType.Data | ApiNotifType.Service;

    private initialized: boolean = false;

    private collectionHandlers: INotifHandler[] = [];

    private recordHandlers: INotifHandler[] = [];

    private dictionaryHandlers: INotifHandler[] = [];

    private paramHandlers: Function[] = [];

    private allHandlers: Function[] = [];

    /* Public Methods */

    public onCollectionMessage(fn: Function): string {
        this.init();
        if (typeof fn === 'function') {
            const name: string = this.getHandlerName();
            this.collectionHandlers.push({ name, fn });
            return name;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public onRecordMessage(fn: Function): string {
        this.init();
        if (typeof fn === 'function') {
            const name: string = this.getHandlerName();
            this.recordHandlers.push({ name, fn });
            return name;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public onDictionaryMessage(fn: Function): string {
        this.init();
        if (typeof fn === 'function') {
            const name: string = this.getHandlerName();
            this.dictionaryHandlers.push({ name, fn });
            return name;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public onParamMessage(handler: Function): void {
        // TODO - Backbone delete
        this.init();
        if (typeof handler === 'function') {
            this.paramHandlers.push(handler);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public onAnyMessage(handler: Function): void {
        // TODO - Backbone delete
        this.init();
        if (typeof handler === 'function') {
            this.allHandlers.push(handler);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public offCollectionMessage(name: string): void {
        this.collectionHandlers = this.collectionHandlers.filter((h: INotifHandler) => {
            return h.name !== name;
        });
    }

    /* Private Public Methods */

    public sendCollectionMessage(message: ICollectionResponse): void {
        setTimeout(() => {
            this.collectionHandlers.forEach(handler => handler.fn.apply(null, [message]));
        }, 1);
    }

    public sendRecordMessage(message: IRecordResponse): void {
        setTimeout(() => {
            this.recordHandlers.forEach(handler => handler.fn.apply(null, [message]));
        }, 1);
    }

    public process(msg: IApiNotifBase): void {
        switch(msg.type) {
            case ApiNotifMsgType.Dico:
                this.processDico(msg as IApiNotifDico);
                break;
            case ApiNotifMsgType.Data:
                this.processData(msg as IApiNotifData);
                break;
        }
        this.allHandlers.forEach(handler => handler.apply(null, [msg]));
    }

    /* Private Methods */

    private init(): void {
        if (!this.initialized) {
            Api.addNotificationHandler(this.process.bind(this), this.NotifType);
            this.initialized = true;
        }
    }

    private processDico(msg: IApiNotifDico): void {
        Collection.onEntityReset(msg.eCNs);
        this.dictionaryHandlers.forEach(handler => handler.fn.apply(null, [Entity.getAll()]));
    }

    private processData(msg: IApiNotifData): void {
        const param: ParamStorage | null = Entity.paramEntity(msg.eCN);
        if (param) {
            this.paramHandlers.forEach(handler => handler.apply(null, [param, msg]));
        } else {
            if (msg.flag.invalidateCache) {
                DocumentsCache.clear(msg.eCN);
                Record.onDocumentsReset(msg.eCN, msg.data);
                Collection.onDocumentsReset(msg.eCN, msg.data);
            } else {
                switch (msg.operation) {
                    case ApiNotifDataOperation.Insert:
                        Collection.onDocumentsCreate(msg.eCN, msg.data);
                        DocumentsCache.documentsCreate(msg.eCN, msg.data);
                        break;
                    case ApiNotifDataOperation.Update:
                        Record.onDocumentsUpdate(msg.eCN, msg.data);
                        Collection.onDocumentsUpdate(msg.eCN, msg.data);
                        DocumentsCache.documentsUpdate(msg.eCN, msg.data);
                        break;
                    case ApiNotifDataOperation.Delete:
                        Collection.onDocumentsDelete(msg.eCN, msg.data);
                        DocumentsCache.documentsDelete(msg.eCN, msg.data);
                        break;
                }
            }
        }
    }

    private getHandlerName(): string {
        return `h_${(new Date()).getTime()}`
    }

}

const Notification = new NotificationInstance();

export default Notification;